import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { FilterSelect } from "@/components/FilterSelect";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Download, Paperclip, FileUp, ChevronDown, MapPin, X, Mail, Inbox, Send, Link2 } from "lucide-react";

const empty = { title: "", company_id: "", direction: "in", date: "", counterpart: "", responsible_person: "", physical_location: "", notes: "", file: null, linked_ids: [] };

export default function Correspondence() {
  const { t } = useI18n();
  const { canEditAny, canEditDoc } = useAuth();
  const qc = useQueryClient();

  const [filters, setFilters] = useState({ company_id: "all", search: "" });
  const [collapsed, setCollapsed] = useState({});
  const toggleSection = (d) => setCollapsed((s) => ({ ...s, [d]: !s[d] }));

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [linkQuery, setLinkQuery] = useState("");

  const { data: companies = [] } = useQuery({ queryKey: ["companies"], queryFn: async () => (await api.get("/companies")).data });
  const companyMap = useMemo(() => Object.fromEntries(companies.map((c) => [c.id, c.name])), [companies]);

  const params = useMemo(() => {
    const p = {};
    if (filters.company_id !== "all") p.company_id = filters.company_id;
    if (filters.search) p.search = filters.search;
    return p;
  }, [filters]);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["correspondence", params],
    queryFn: async () => (await api.get("/correspondence", { params })).data,
  });
  const corrById = useMemo(() => Object.fromEntries(items.map((c) => [c.id, c])), [items]);

  const saveMut = useMutation({
    mutationFn: async (payload) => {
      if (editing) return (await api.put(`/correspondence/${editing}`, payload)).data;
      return (await api.post("/correspondence", payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["correspondence"] });
      setModalOpen(false);
      toast.success(t("save") + " ✓");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/correspondence/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["correspondence"] }); toast.success("Deleted"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const openNew = (direction) => { setEditing(null); setForm({ ...empty, direction }); setLinkQuery(""); setModalOpen(true); };
  const openEdit = (c) => {
    setEditing(c.id);
    setForm({
      title: c.title, company_id: c.company_id, direction: c.direction,
      date: c.date || "", counterpart: c.counterpart || "", responsible_person: c.responsible_person || "",
      physical_location: c.physical_location || "", notes: c.notes || "", file: c.file || null,
      linked_ids: c.linked_ids || [],
    });
    setLinkQuery("");
    setModalOpen(true);
  };

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, file: data }));
      toast.success(f.name);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const toggleLink = (id) => setForm((s) => {
    const has = s.linked_ids.includes(id);
    return { ...s, linked_ids: has ? s.linked_ids.filter((x) => x !== id) : [...s.linked_ids, id] };
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.company_id) { toast.error(t("required")); return; }
    setSaving(true);
    const payload = { ...form, date: form.date || null };
    await saveMut.mutateAsync(payload).catch(() => {});
    setSaving(false);
  };

  const linkCandidates = items.filter((c) => c.id !== editing).filter((c) => {
    const q = linkQuery.trim().toLowerCase();
    if (!q) return true;
    return (c.title || "").toLowerCase().includes(q) || (companyMap[c.company_id] || "").toLowerCase().includes(q);
  });

  const Section = ({ direction, icon: Icon, label }) => {
    const list = items.filter((c) => c.direction === direction);
    const isOpen = !collapsed[direction];
    return (
      <Card className="overflow-hidden" data-testid={`corr-section-${direction}`}>
        <div className="flex items-center gap-2 border-b bg-secondary/40 px-4 py-3">
          <button type="button" onClick={() => toggleSection(direction)} data-testid={`corr-toggle-${direction}`} aria-expanded={isOpen} className="flex flex-1 items-center gap-2 text-left">
            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? "" : "-rotate-90"}`} />
            <Icon className="h-4 w-4 text-horizonte-orange" />
            <h3 className="font-display text-sm font-semibold uppercase tracking-wide">{label}</h3>
            <span className="rounded-full bg-horizonte-orange/15 px-2 py-0.5 text-xs font-bold text-horizonte-orange">{list.length}</span>
          </button>
          {canEditAny && (
            <Button size="sm" variant="outline" onClick={() => openNew(direction)} data-testid={`add-corr-${direction}`} className="gap-1 border-horizonte-orange/40 text-horizonte-orange hover:bg-horizonte-orange/10">
              <Plus className="h-4 w-4" /> {t("addCorrespondence")}
            </Button>
          )}
        </div>
        {isOpen && (
          list.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground" data-testid={`no-corr-${direction}`}>{t("noCorrespondence")}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid={`corr-table-${direction}`}>
                <thead className="border-b bg-secondary/50">
                  <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-3 font-semibold">{t("title")}</th>
                    <th className="px-4 py-3 font-semibold">{t("company")}</th>
                    <th className="px-4 py-3 font-semibold">{t("counterpart")}</th>
                    <th className="px-4 py-3 font-semibold">{t("corrDate")}</th>
                    <th className="px-4 py-3 font-semibold">{t("responsible")}</th>
                    <th className="px-4 py-3 font-semibold">{t("physicalLocation")}</th>
                    <th className="px-4 py-3 text-right font-semibold">{t("actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {list.map((c) => {
                    const links = (c.linked_ids || []).map((id) => corrById[id]).filter(Boolean);
                    return (
                      <tr key={c.id} data-testid={`corr-row-${c.id}`} className="transition-colors hover:bg-secondary/40">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 font-medium">
                            {c.file && <Paperclip className="h-3.5 w-3.5 text-horizonte-orange" />}
                            {c.title}
                          </div>
                          {links.length > 0 && (
                            <div className="mt-1 flex flex-wrap items-center gap-1" data-testid={`corr-links-${c.id}`}>
                              <Link2 className="h-3 w-3 text-muted-foreground" />
                              {links.map((l) => (
                                <span key={l.id} className="rounded bg-secondary px-1.5 py-0.5 text-xs text-muted-foreground">{l.direction === "in" ? "↙" : "↗"} {l.title}</span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{companyMap[c.company_id] || "-"}</td>
                        <td className="px-4 py-3 text-muted-foreground">{c.counterpart || "—"}</td>
                        <td className="px-4 py-3 font-mono text-xs">{c.date || "—"}</td>
                        <td className="px-4 py-3 text-muted-foreground">{c.responsible_person || "—"}</td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {c.physical_location ? (<span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-horizonte-orange" />{c.physical_location}</span>) : "—"}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            {c.file && (
                              <a href={fileUrl(c.file.storage_path)} target="_blank" rel="noreferrer" data-testid={`corr-download-${c.id}`} title={t("download")}>
                                <Button size="icon" variant="ghost" className="h-8 w-8"><Download className="h-4 w-4" /></Button>
                              </a>
                            )}
                            {canEditDoc(c.company_id, "correspondence") && (
                              <>
                                <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`corr-edit-${c.id}`} onClick={() => openEdit(c)} title={t("editLabel")}><Pencil className="h-4 w-4" /></Button>
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`corr-delete-${c.id}`} onClick={() => { if (window.confirm(t("confirmDelete"))) delMut.mutate(c.id); }} title={t("delete")}><Trash2 className="h-4 w-4" /></Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">
          <Mail className="h-7 w-7 text-horizonte-orange" /> {t("correspondence")}
        </h1>
        <p className="mt-1 text-muted-foreground">{t("correspondenceSub")}</p>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="relative sm:col-span-2">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="corr-search-input" className="pl-9" placeholder={t("search")} value={filters.search} onChange={(e) => setFilters((s) => ({ ...s, search: e.target.value }))} />
          </div>
          <FilterSelect testId="corr-filter-company" value={filters.company_id} onChange={(v) => setFilters((s) => ({ ...s, company_id: v }))} placeholder={t("allCompanies")} options={[{ value: "all", label: t("allCompanies") }, ...companies.map((c) => ({ value: c.id, label: c.name }))]} />
        </div>
      </Card>

      {isLoading ? (
        <Card className="p-10 text-center text-muted-foreground">…</Card>
      ) : (
        <div className="space-y-6">
          <Section direction="in" icon={Inbox} label={t("correspondenceIn")} />
          <Section direction="out" icon={Send} label={t("correspondenceOut")} />
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="corr-modal">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? t("editCorrespondence") : t("addCorrespondence")}</DialogTitle>
            <DialogDescription>{t("correspondenceSub")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <Field label={t("title")}><Input data-testid="corr-title-input" value={form.title} onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))} required /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("company")}>
                <Select value={form.company_id} onValueChange={(v) => setForm((s) => ({ ...s, company_id: v }))}>
                  <SelectTrigger data-testid="corr-company-select"><SelectValue placeholder={t("company")} /></SelectTrigger>
                  <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label={t("direction")}>
                <Select value={form.direction} onValueChange={(v) => setForm((s) => ({ ...s, direction: v }))}>
                  <SelectTrigger data-testid="corr-direction-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in">{t("dirIn")}</SelectItem>
                    <SelectItem value="out">{t("dirOut")}</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("corrDate")}><Input type="date" data-testid="corr-date-input" value={form.date} onChange={(e) => setForm((s) => ({ ...s, date: e.target.value }))} /></Field>
              <Field label={t("counterpart")}><Input data-testid="corr-counterpart-input" value={form.counterpart} onChange={(e) => setForm((s) => ({ ...s, counterpart: e.target.value }))} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("responsible")}><Input data-testid="corr-responsible-input" value={form.responsible_person} onChange={(e) => setForm((s) => ({ ...s, responsible_person: e.target.value }))} /></Field>
              <Field label={t("physicalLocation")}><Input data-testid="corr-physical-input" value={form.physical_location} onChange={(e) => setForm((s) => ({ ...s, physical_location: e.target.value }))} placeholder={t("physicalLocationPlaceholder")} /></Field>
            </div>
            <Field label={t("notes")}><Textarea data-testid="corr-notes-input" value={form.notes} onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))} rows={2} /></Field>
            <Field label={t("attachment")}>
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed px-3 py-2.5 text-sm transition-colors hover:border-horizonte-orange hover:bg-secondary/40" data-testid="corr-file-label">
                {uploading ? <span className="text-muted-foreground">…</span> : <FileUp className="h-4 w-4 text-horizonte-orange" />}
                <span className="truncate">{form.file ? form.file.original_filename : t("uploadFile")}</span>
                <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" onChange={handleUpload} data-testid="corr-file-input" />
              </label>
            </Field>

            <Field label={t("linkedCorrespondence")}>
              {form.linked_ids.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-1.5" data-testid="corr-selected-links">
                  {form.linked_ids.map((id) => {
                    const l = corrById[id];
                    return (
                      <span key={id} className="inline-flex items-center gap-1 rounded-md bg-horizonte-orange/10 px-2 py-1 text-xs font-medium" data-testid={`corr-link-chip-${id}`}>
                        {l ? `${l.direction === "in" ? "↙" : "↗"} ${l.title}` : id}
                        <button type="button" onClick={() => toggleLink(id)} className="text-muted-foreground hover:text-foreground"><X className="h-3 w-3" /></button>
                      </span>
                    );
                  })}
                </div>
              )}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input className="pl-9" placeholder={t("linkSearch")} value={linkQuery} onChange={(e) => setLinkQuery(e.target.value)} data-testid="corr-link-search" />
              </div>
              {linkQuery.trim() && (
                <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border" data-testid="corr-link-list">
                  {linkCandidates.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">{t("noReplacementFound")}</div>}
                  {linkCandidates.slice(0, 50).map((c) => {
                    const sel = form.linked_ids.includes(c.id);
                    return (
                      <button type="button" key={c.id} data-testid={`corr-link-option-${c.id}`} onClick={() => toggleLink(c.id)} className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-secondary/60 ${sel ? "bg-horizonte-orange/5" : ""}`}>
                        <span className="truncate">{c.direction === "in" ? "↙" : "↗"} {c.title}</span>
                        <span className="ml-2 shrink-0 text-xs text-muted-foreground">{companyMap[c.company_id]}{sel ? " ✓" : ""}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </Field>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)} data-testid="corr-cancel-button">{t("cancel")}</Button>
              <Button type="submit" disabled={saving || uploading} data-testid="corr-save-button" className="bg-renco-navy text-white hover:bg-renco-slate">{saving ? t("saving") : t("save")}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;
}
